import React, { useState, useEffect, useRef } from 'react';
import CourseSelector from '../activity-creator/components/CourseSelector';
import CourseGroupSelector from '../activity-creator/components/CourseGroupSelector';
import ServiceAccountSelector from '../activity-creator/components/ServiceAccountSelector';
import CredentialsForm from '../activity-creator/components/CredentialsForm';
import ArtWorkoutOtpForm from '../activity-creator/components/ArtWorkoutOtpForm';
import { useCredentialsValidation } from '../activity-creator/hooks/useCredentialsValidation';
import { updateActivity } from './api/activityEditorApi';
import { updateServiceAccount } from '../activity-creator/api/activityApi';
import { initI18n } from '../../shared/i18n';

/**
 * ActivityEditor - Main component for editing curriculum activities
 * Handles editing external service activities (CodeCombat, Duolingo, Khan Academy)
 * and other activity types (Physical, etc.)
 */
// Volume options with multipliers
const VOLUME_OPTIONS = [
  { value: 'low', multiplier: 1 },
  { value: 'moderate', multiplier: 2 },
  { value: 'high', multiplier: 4 }
];

const ActivityEditor = ({
  childId,
  childName,
  activityId,
  activityType,
  activityName,
  iconUrl,
  configFields,
  existingCredentials,
  existingCourses,
  existingGroupAlias,
  existingConfig,
  existingVolume = 'low',
  courseStructure: initialCourseStructure,
  isExternalService: isExternalServiceProp = false,
  railsEnv,
  csrfToken,
  locale = 'vi',
  onComplete,
  onCancel
}) => {
  // Use ref to track if i18n has been initialized to prevent infinite re-renders
  const i18nInitialized = useRef(false);

  // Initialize i18n once on mount
  useEffect(() => {
    if (!i18nInitialized.current) {
      initI18n(locale);
      i18nInitialized.current = true;
    }
  }, [locale]);

  const isTestMode = railsEnv === 'test' || railsEnv === 'development';

  // Centralized activity type configuration
  const activityConfig = {
    codecombat: {
      hasCourseSelection: true,
      usesCourseGroups: true,
      maxCourses: 0,
      canEditCourses: true,
      hasUpdateButton: true,
    },
    duolingo: {
      hasCourseSelection: true,
      usesCourseGroups: false,
      maxCourses: 3,
      canEditCourses: false, // Duolingo courses are fixed after creation
      hasUpdateButton: false,
    },
    khan_academy: {
      hasCourseSelection: true,
      usesCourseGroups: false,
      maxCourses: 2,
      canEditCourses: true,
      hasUpdateButton: true,
    },
    brilliant: {
      hasCourseSelection: false, // Brilliant has no course selection
      usesCourseGroups: false,
      maxCourses: 0,
      canEditCourses: false,
      hasUpdateButton: false, // Only account change is allowed
    },
    art_workout: {
      hasCourseSelection: false, // ArtWorkout has no course selection (single course)
      usesCourseGroups: false,
      maxCourses: 0,
      canEditCourses: false,
      hasUpdateButton: true, // Show update button to save PRO flag changes
    },
    vmonkey: {
      hasCourseSelection: false, // VMonkey has no course selection (single course)
      usesCourseGroups: false,
      maxCourses: 0,
      canEditCourses: false,
      hasUpdateButton: false, // Only account change is allowed
    },
    mimo: {
      hasCourseSelection: false, // Mimo has no course selection (auto-course)
      usesCourseGroups: false,
      maxCourses: 0,
      canEditCourses: false,
      hasUpdateButton: false, // Only account change is allowed
    },
    get_epic: {
      hasCourseSelection: false, // GetEpic has no course selection (auto-course)
      usesCourseGroups: false,
      maxCourses: 0,
      canEditCourses: false,
      hasUpdateButton: false, // Only account change is allowed
    },
  };

  const config = activityConfig[activityType] || {};
  const showCourseSelection = config.hasCourseSelection && config.canEditCourses;
  const showUpdateButton = config.hasUpdateButton;

  // State management
  const [credentials, setCredentials] = useState(existingCredentials || {});
  const [selectedCourses, setSelectedCourses] = useState(existingCourses || []);
  const [selectedGroupAlias, setSelectedGroupAlias] = useState(existingGroupAlias || null);
  const [courseStructure, setCourseStructure] = useState(initialCourseStructure || {});
  const [volume, setVolume] = useState(existingVolume || 'low');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [credentialsUpdateSuccess, setCredentialsUpdateSuccess] = useState(false);
  const [credentialsChanged, setCredentialsChanged] = useState(false);
  const [configValues, setConfigValues] = useState(existingConfig || {});

  // Account change flow state
  // 'display' = showing current credentials, 'select_account' = choosing account type, 'enter_credentials' = entering custom credentials
  const [accountChangeStep, setAccountChangeStep] = useState('display');
  const [usePreloadedAccount, setUsePreloadedAccount] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState({});

  // Credentials validation (for changing credentials)
  const {
    isValidating,
    validationError,
    validationSuccess,
    courseStructure: newCourseStructure,
    validateCredentials,
    clearValidationState
  } = useCredentialsValidation(csrfToken);

  // Use server-provided is_external_service flag (centralized in ServiceQueryStrategies::Factory)
  const isExternalService = isExternalServiceProp;

  // For Duolingo, courses cannot be changed when editing (only account can be changed)
  const isDuolingoEditMode = activityType === 'duolingo' && existingCourses && existingCourses.length > 0;

  // Handle course toggle
  const handleCourseToggle = (courses) => {
    setSelectedCourses(courses);
  };

  // Handle group selection (for CodeCombat)
  const handleGroupSelect = (groupAlias) => {
    setSelectedGroupAlias(groupAlias);
    // Find the group from courseStructure and set its courses as selected
    const group = courseStructure?.find(g => g.alias === groupAlias);
    if (group) {
      setSelectedCourses(group.courses || []);
    }
  };

  // Handle change account button click - show account selection
  // ArtWorkout uses OTP-based authentication, go directly to OTP form
  const handleChangeAccountClick = () => {
    if (activityType === 'art_workout') {
      setAccountChangeStep('art_workout_otp');
    } else {
      setAccountChangeStep('select_account');
    }
    clearValidationState();
    setCredentialsUpdateSuccess(false);
  };

  // Handle account selection from ServiceAccountSelector
  // details = credentials, isCustom = true if guardian account, isPreloaded = true if powerkid account
  const handleAccountReady = async (details, isCustom = false, isPreloaded = false) => {
    if (isCustom) {
      // User chose custom/guardian account - go to credentials form
      setUsePreloadedAccount(false);
      clearValidationState();
      // Prefill with guardian_account_details if available
      // Pass through all credential fields (username/email/password)
      if (details) {
        setPendingCredentials({...details});
      } else {
        setPendingCredentials({});
      }
      setAccountChangeStep('enter_credentials');
    } else if (details) {
      // User chose preloaded account - validate directly
      setUsePreloadedAccount(true);
      // Pass through all credential fields (username/email/password)
      const creds = {...details};
      setPendingCredentials(creds);

      // activityType for editing is already the service name (duolingo, khan_academy, etc.)
      const result = await validateCredentials(creds, activityType);
      if (result.success) {
        // Update credentials and course structure, stay on edit page
        setCredentials(creds);
        const nextStructure = resolveCourseStructure(result);
        setCourseStructure(nextStructure);
        if (config.canEditCourses) {
          setSelectedCourses(coursesForGroup(nextStructure, selectedGroupAlias));
        }
        setCredentialsChanged(true);
        setAccountChangeStep('display');
      }
      // If validation fails, stay on select_account step - error will be shown
    }
  };

  // Handle credentials form submission (for custom accounts)
  const handleCredentialsFormValidate = async (creds) => {
    setPendingCredentials(creds);
    // activityType for editing is already the service name (duolingo, khan_academy, etc.)
    const result = await validateCredentials(creds, activityType);

    if (result.success) {
      // Update credentials and course structure, stay on edit page
      setCredentials(creds);
      const nextStructure = resolveCourseStructure(result);
      setCourseStructure(nextStructure);
      if (config.canEditCourses) {
        setSelectedCourses(coursesForGroup(nextStructure, selectedGroupAlias));
      }
      setCredentialsChanged(true);
      setUsePreloadedAccount(false);
      setAccountChangeStep('display');
    }
  };

  // CodeCombat keeps its previously-selected group across an account change,
  // so re-populate the underlying courses from the refreshed structure. When
  // the previous group no longer exists (or for services without groups),
  // return [] so the guardian is forced to re-pick.
  const coursesForGroup = (structure, groupAlias) => {
    if (!groupAlias || !Array.isArray(structure)) return [];
    const group = structure.find(g => g.alias === groupAlias);
    return group?.courses || [];
  };

  // CodeCombat's validate_credentials response uses `course_groups` (array),
  // while Duolingo/Khan use `course_structure` (object). Fall back to the
  // existing structure so we never blank it out — a nil/empty replacement
  // would crash CourseGroupSelector.map on the display step.
  const resolveCourseStructure = (result) => {
    if (result.courseGroups && result.courseGroups.length > 0) {
      return result.courseGroups;
    }
    if (result.courseStructure && Object.keys(result.courseStructure).length > 0) {
      return result.courseStructure;
    }
    return courseStructure;
  };

  // Handle back from credentials form to account selection
  const handleBackToAccountSelection = () => {
    setAccountChangeStep('select_account');
    setPendingCredentials({});
    clearValidationState();
  };

  // Handle cancel account change - go back to displaying current credentials
  const handleCancelAccountChange = () => {
    setAccountChangeStep('display');
    setPendingCredentials({});
    setUsePreloadedAccount(false);
    clearValidationState();
  };

  // Handle ArtWorkout OTP success - immediately updates service account with new tokens
  const handleArtWorkoutOtpSuccess = async ({ credentials }) => {
    // Immediately save tokens to service account (tokens are time-sensitive)
    try {
      await updateServiceAccount(childId, activityType, false, credentials, csrfToken, null);
    } catch (err) {
      setSubmitError(err.message);
      return;
    }

    setCredentials(credentials);
    // Don't mark as changed since we already saved
    setCredentialsChanged(false);
    setUsePreloadedAccount(false);
    setAccountChangeStep('display');
    // Show success message
    setCredentialsUpdateSuccess(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate course selection for activities with course selection
      if (showCourseSelection && selectedCourses.length === 0) {
        setSubmitError('Vui lòng chọn ít nhất một khóa học');
        setIsSubmitting(false);
        return;
      }

      if (config.maxCourses > 0 && selectedCourses.length > config.maxCourses) {
        setSubmitError(`Bạn chỉ có thể chọn tối đa ${config.maxCourses} khóa học cho ${activityName}`);
        setIsSubmitting(false);
        return;
      }

      // Update service account if credentials were changed
      if (isExternalService && credentialsChanged) {
        try {
          if (usePreloadedAccount) {
            await updateServiceAccount(childId, activityType, true, null, csrfToken, null);
          } else {
            await updateServiceAccount(childId, activityType, false, credentials, csrfToken, null);
          }
        } catch (err) {
          setSubmitError(err.message);
          setIsSubmitting(false);
          return;
        }
      }

      const activityData = {
        activity_type: activityType,
        volume: volume
      };

      // Add external config for external services
      if (isExternalService) {
        activityData.external_config = {
          ...credentials,
          courses: selectedCourses
        };
        // Add group_alias for CodeCombat
        if (activityType === 'codecombat' && selectedGroupAlias) {
          activityData.external_config.group_alias = selectedGroupAlias;
        }
      } else if (configFields && configFields.length > 0) {
        // For non-external services with config fields (e.g., physical)
        activityData.external_config = configValues;
      }

      const result = await updateActivity(childId, activityId, activityData, csrfToken);

      setIsSubmitting(false);

      if (onComplete) {
        onComplete(result);
      }
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
    }
  };

  // Get field label from configFields
  const getFieldLabel = (fieldName) => {
    const field = configFields?.find(f => f.name === fieldName);
    return field?.label || fieldName;
  };

  // Render credential display (read-only with change button)
  const renderCredentialsDisplay = () => {
    const requiresPassword = activityType === 'khan_academy' || activityType === 'brilliant' || activityType === 'mimo' || activityType === 'get_epic';
    const requiresProfileName = activityType === 'get_epic';
    // Brilliant, ArtWorkout, VMonkey, Mimo, and GetEpic use email, others use username
    const primaryFieldName = (activityType === 'brilliant' || activityType === 'art_workout' || activityType === 'vmonkey' || activityType === 'mimo' || activityType === 'get_epic') ? 'email' : 'username';
    const primaryFieldLabel = getFieldLabel(primaryFieldName);
    const passwordLabel = getFieldLabel('password');
    const profileNameLabel = getFieldLabel('profile_name');
    // All activities can change accounts (ArtWorkout uses OTP re-authentication)
    const canChangeAccount = true;

    const primaryColClass = requiresProfileName ? 'col-md-4' : (requiresPassword ? 'col-md-6' : 'col-12');
    const passwordColClass = requiresProfileName ? 'col-md-4' : 'col-md-6';

    return (
      <div className="mb-4">
        <div className="bg-light border rounded-3 p-3">
          <div className="row g-3 mb-3">
            {/* Primary credential display (username or email) */}
            <div className={primaryColClass}>
              <label htmlFor="credentials-primary-input" className="form-label small fw-medium text-dark mb-2" style={{ fontSize: '0.75rem' }}>
                {primaryFieldLabel}
              </label>
              <input
                type="text"
                id="credentials-primary-input"
                className="form-control bg-light border text-muted"
                value={credentials[primaryFieldName] || ''}
                readOnly
                data-field-name={primaryFieldName}
                style={{ fontSize: '0.875rem' }}
                {...(isTestMode && { 'data-testid': 'credentials-primary-display' })}
              />
            </div>

            {/* Password display */}
            {requiresPassword && (
              <div className={passwordColClass}>
                <label htmlFor="credentials-password-input" className="form-label small fw-medium text-dark mb-2" style={{ fontSize: '0.75rem' }}>
                  {passwordLabel}
                </label>
                <input
                  type="password"
                  id="credentials-password-input"
                  className="form-control bg-light border text-muted"
                  value={credentials.password || '••••••••'}
                  readOnly
                  data-field-name="password"
                  style={{ fontSize: '0.875rem' }}
                  {...(isTestMode && { 'data-testid': 'credentials-password-display' })}
                />
              </div>
            )}

            {/* Profile name display (GetEpic) */}
            {requiresProfileName && (
              <div className="col-md-4">
                <label htmlFor="credentials-profile-name-input" className="form-label small fw-medium text-dark mb-2" style={{ fontSize: '0.75rem' }}>
                  {profileNameLabel}
                </label>
                <input
                  type="text"
                  id="credentials-profile-name-input"
                  className="form-control bg-light border text-muted"
                  value={credentials.profile_name || ''}
                  readOnly
                  data-field-name="profile_name"
                  style={{ fontSize: '0.875rem' }}
                  {...(isTestMode && { 'data-testid': 'credentials-profile-name-display' })}
                />
              </div>
            )}
          </div>

          {/* Change Account Button - not shown for ArtWorkout (requires OTP re-authentication) */}
          {canChangeAccount && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary border"
                onClick={handleChangeAccountClick}
                {...(isTestMode && { 'data-testid': 'change-credentials-btn' })}
              >
                Đổi Tài Khoản
              </button>
            </div>
          )}
        </div>

        {/* Show success message when credentials changed */}
        {credentialsChanged && (
          <div className="alert alert-success mt-2" role="alert">
            {!showCourseSelection
              ? 'Thông tin đăng nhập đã được cập nhật.'
              : 'Thông tin đăng nhập đã được cập nhật. Vui lòng chọn khóa học.'}
          </div>
        )}
      </div>
    );
  };

  // Render account selection step (ServiceAccountSelector in change mode)
  const renderAccountSelection = () => {
    return (
      <div className="mb-4">
        <ServiceAccountSelector
          childId={childId}
          childUsername={childName}
          activityType={activityType}
          activityName={activityName}
          csrfToken={csrfToken}
          onAccountReady={handleAccountReady}
          onBack={handleCancelAccountChange}
          isTestMode={isTestMode}
          isValidating={isValidating}
          startInChangeMode={true}
        />
        {validationError && (
          <div
            className="alert alert-danger mt-3"
            role="alert"
            {...(isTestMode && { 'data-testid': usePreloadedAccount ? 'preloaded-account-error' : 'credentials-error' })}
          >
            <div className="d-flex">
              <div className="flex-shrink-0 me-2">
                <i className="bi bi-exclamation-triangle-fill"></i>
              </div>
              <div>
                {usePreloadedAccount ? (
                  <>
                    <strong>Tài khoản Powerkid cung cấp không thể xác thực</strong>
                    <p className="mb-0 mt-1 small">
                      Vui lòng chọn sử dụng tài khoản riêng của bạn.
                    </p>
                  </>
                ) : (
                  validationError
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render credentials form step (for custom/guardian accounts)
  const renderCredentialsFormStep = () => {
    return (
      <div className="mb-4">
        <CredentialsForm
          activityType={activityType}
          activityName={activityName}
          configFields={configFields || []}
          onValidate={handleCredentialsFormValidate}
          isValidating={isValidating}
          error={validationError}
          success={validationSuccess}
          isTestMode={isTestMode}
          initialValues={pendingCredentials}
          submitButtonText={showCourseSelection ? 'Chọn Khóa Học' : 'Cập Nhật'}
        />
        {/* Back Button */}
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-link text-secondary p-0"
            onClick={handleBackToAccountSelection}
            disabled={isValidating}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Quay Lại Chọn Tài Khoản
          </button>
        </div>
      </div>
    );
  };

  // Render ArtWorkout OTP form (for account change)
  const renderArtWorkoutOtpForm = () => {
    return (
      <div className="mb-4">
        <ArtWorkoutOtpForm
          childId={childId}
          csrfToken={csrfToken}
          onSuccess={handleArtWorkoutOtpSuccess}
          isTestMode={isTestMode}
          initialEmail={credentials.email || ''}
        />
        {/* Cancel Button */}
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-link text-secondary p-0"
            onClick={handleCancelAccountChange}
            {...(isTestMode && { 'data-testid': 'cancel-otp-btn' })}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Hủy
          </button>
        </div>
      </div>
    );
  };

  // Render config fields for non-external services (e.g., physical activity)
  const renderConfigFields = () => {
    if (!configFields || configFields.length === 0) return null;

    return (
      <div className="mb-4">
        {configFields.map((field) => {
          if (field.type === 'select') {
            return (
              <div key={field.name} className="mb-3">
                <label
                  htmlFor={`config-${field.name}`}
                  className="form-label fw-semibold"
                >
                  {field.label}
                </label>
                <select
                  id={`config-${field.name}`}
                  className="form-select"
                  value={configValues[field.name] || field.default || ''}
                  onChange={(e) => setConfigValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                  required={field.required}
                  {...(isTestMode && { 'data-testid': `config-${field.name}` })}
                >
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {field.help_text && (
                  <small className="form-text text-muted d-block mt-1">
                    {field.help_text}
                  </small>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  // Render fallback icon
  const fallbackIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='24' fill='%236c757d'%3E${activityName?.[0] || 'A'}%3C/text%3E%3C/svg%3E`;

  return (
    <div className="activity-editor bg-light min-vh-100">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border rounded-3">
              <div className="card-body p-4">
                {/* Header with Icon */}
                <div className="d-flex align-items-start mb-4">
                  <div className="flex-shrink-0 me-3">
                    <div className="rounded-2 overflow-hidden" style={{ width: '64px', height: '64px' }}>
                      <img
                        src={iconUrl || fallbackIcon}
                        alt={activityName}
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => { e.target.src = fallbackIcon; }}
                      />
                    </div>
                  </div>
                  <div>
                    <h2 className="h4 fw-bold mb-1">
                      Cấu Hình {activityName}
                    </h2>
                    <p className="text-muted mb-0">
                      Cập nhật cấu hình hoạt động cho {childName}
                    </p>
                  </div>
                </div>

                {/* External Service Form */}
                {isExternalService && (
                  <>
                    {/* Account Change Flow */}
                    {accountChangeStep === 'display' && (
                      <>
                        {/* Credentials Display */}
                        {renderCredentialsDisplay()}

                        {/* Volume Selector */}
                        <div className="mb-4">
                          <label className="form-label fw-semibold">
                            Khối lượng học tập
                          </label>
                          <div className="btn-group w-100" role="group" aria-label="Volume selector">
                            {VOLUME_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`btn ${volume === option.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setVolume(option.value)}
                                {...(isTestMode && { 'data-testid': `volume-${option.value}` })}
                              >
                                <div className="d-flex flex-column align-items-center">
                                  <span className="fw-medium">
                                    {option.value === 'low' && 'Thấp'}
                                    {option.value === 'moderate' && 'Vừa'}
                                    {option.value === 'high' && 'Cao'}
                                  </span>
                                  <small className="text-muted">x{option.multiplier}</small>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Course Selection - Use group selector for CodeCombat, regular for others */}
                        {/* For Duolingo/Brilliant in edit mode, courses cannot be changed - only account can be changed */}
                        {activityType === 'codecombat' ? (
                          <CourseGroupSelector
                            courseGroups={courseStructure}
                            selectedGroupAlias={selectedGroupAlias}
                            onGroupSelect={handleGroupSelect}
                            isLoading={false}
                            error={submitError}
                            isTestMode={isTestMode}
                          />
                        ) : showCourseSelection && (
                          <CourseSelector
                            activityType={activityType}
                            activityName={activityName}
                            courses={[]}
                            courseStructure={courseStructure}
                            maxCourses={config.maxCourses}
                            selectedCourses={selectedCourses}
                            onCourseToggle={handleCourseToggle}
                            isLoading={false}
                            error={submitError}
                            isTestMode={isTestMode}
                          />
                        )}
                      </>
                    )}

                    {/* Account Selection Step */}
                    {accountChangeStep === 'select_account' && renderAccountSelection()}

                    {/* Credentials Form Step (for custom/guardian accounts) */}
                    {accountChangeStep === 'enter_credentials' && renderCredentialsFormStep()}

                    {/* ArtWorkout OTP Form Step (for OTP-based account change) */}
                    {accountChangeStep === 'art_workout_otp' && renderArtWorkoutOtpForm()}
                  </>
                )}

                {/* Non-External Service Config Fields (e.g., Physical) */}
                {!isExternalService && renderConfigFields()}

                {/* Volume Selector for Non-External Services */}
                {!isExternalService && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Khối lượng học tập
                    </label>
                    <div className="btn-group w-100" role="group" aria-label="Volume selector">
                      {VOLUME_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`btn ${volume === option.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setVolume(option.value)}
                          {...(isTestMode && { 'data-testid': `volume-${option.value}` })}
                        >
                          <div className="d-flex flex-column align-items-center">
                            <span className="fw-medium">
                              {option.value === 'low' && 'Thấp'}
                              {option.value === 'moderate' && 'Vừa'}
                              {option.value === 'high' && 'Cao'}
                            </span>
                            <small className="text-muted">x{option.multiplier}</small>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Credentials Update Success Message */}
                {credentialsUpdateSuccess && (
                  <div className="alert alert-success mt-3" role="alert" {...(isTestMode && { 'data-testid': 'credentials-update-success' })}>
                    Thông tin đăng nhập đã được cập nhật
                  </div>
                )}

                {/* Error Message */}
                {submitError && (
                  <div className="alert alert-danger mt-3" role="alert" {...(isTestMode && { 'data-testid': 'submit-error' })}>
                    {submitError}
                  </div>
                )}

                {/* Form Actions - only show when in display mode (not during account change) */}
                {/* Always show update button since volume can be changed for all activities */}
                {(!isExternalService || accountChangeStep === 'display') && (
                  <div className="d-flex justify-content-end gap-3 pt-3">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a
                      href="#"
                      className="btn btn-outline-secondary"
                      onClick={(e) => { e.preventDefault(); onCancel(); }}
                    >
                      Hủy
                    </a>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting || (isExternalService && !isDuolingoEditMode && config.hasCourseSelection && selectedCourses.length === 0)}
                      {...(isTestMode && { 'data-testid': 'update-activity-btn' })}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Đang cập nhật...</span>
                          </span>
                          Đang Cập Nhật...
                        </>
                      ) : (
                        'Cập Nhật Hoạt Động'
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Back Link - only show when in display mode */}
            {(!isExternalService || accountChangeStep === 'display') && (
              <div className="mt-3">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  href="#"
                  className="btn btn-link text-secondary"
                  onClick={(e) => { e.preventDefault(); onCancel(); }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Quay Lại Chương Trình Học
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityEditor;
